import React, { useEffect, useMemo, useState } from 'react';
import ApiService from '../../../../services/apiService';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';

const numberFmt = (n, digits = 2) =>
  new Intl.NumberFormat('en-GB', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(Number.isFinite(+n) ? +n : 0);

const dateToInputValue = (d) => new Date(d).toISOString().slice(0, 16);

const COLUMNS = [
  { key: 'Payment Category', label: 'Payment Category' },
  { key: 'Total Sales (Qty)', label: 'Total Sales (Qty)', align: 'right' },
  { key: 'Total Sales (Amount)', label: 'Total Sales (Amount)', align: 'right' },
  { key: '% of Total Sales', label: '% of Total Sales', align: 'right', suffix: '%' },
];

const clean = (v) => (v && typeof v === 'object' ? 0 : (v ?? 0));

// ⭐ PaymentType mapping:
// 0 = All
// 1 = Cash
// 8 = Card
// 9 = EFT
// 10 = Account
const PAYMENT_TYPE_OPTIONS = [
  { value: 0, label: 'All Payment Types' },
  { value: 8, label: 'Card' },
  { value: 1, label: 'Cash' },
  { value: 9, label: 'EFT' },
  { value: 10, label: 'Account' },
];

// ✅ BranchId from localStorage (supports multiple shapes)
// - If localStorage has "resBranchId" as a number, it will use that.
// - Else if it has "resBranch" as JSON, it will try .id / .branchId
const getResBranchId = () => {
  const direct = Number(localStorage.getItem('BranchId') || 0);
  if (direct) return direct;

  try {
    const raw = localStorage.getItem('BranchId');
    const obj = raw ? JSON.parse(raw) : null;
    const id = Number(obj?.id || obj?.branchId || 0);
    return id || 0;
  } catch {
    return 0;
  }
};

const CategorySaleReportCashier = ({ Currency }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);

  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 100,
    totalRecords: 0,
    totalPages: 0,
  });

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const now = new Date();
  const tenDaysAgo = new Date(now);
  tenDaysAgo.setDate(now.getDate() - 10);

  const [formData, setFormData] = useState({
    fromDate: dateToInputValue(tenDaysAgo),
    toDate: dateToInputValue(now),
  });

  // ⭐ Payment type state
  const [paymentType, setPaymentType] = useState(PAYMENT_TYPE_OPTIONS[0]); // default: All

  const safeRows = useMemo(() => (Array.isArray(rows) ? rows : []), [rows]);

  const totals = useMemo(() => {
    const qty = safeRows.reduce((s, r) => s + (+clean(r['Total Sales (Qty)']) || 0), 0);
    const amt = safeRows.reduce((s, r) => s + (+clean(r['Total Sales (Amount)']) || 0), 0);
    const pct = safeRows.reduce((s, r) => s + (+clean(r['% of Total Sales']) || 0), 0);
    return { qty, amt, pct };
  }, [safeRows]);

  // -----------------------------------------
  // ✅ Fetch Report Data (NO branch dropdown)
  // -----------------------------------------
  const fetchData = async (page = 1) => {
    setLoading(true);
    setErr('');

    try {
      const resBranchId = getResBranchId();

      // If BranchId is mandatory, fail fast with a clear message
      if (!resBranchId) {
        setErr('Branch is not set in localStorage. Please login again.');
        setRows([]);
        setPagination((p) => ({ ...p, currentPage: 1 }));
        return;
      }

      const apiService = new ApiService();
      const params = new URLSearchParams();

      params.append('ReportType', 'Payment Category Summary');
      params.append('Measure', 'Revenue');
      params.append('StartDate', formData.fromDate);
      params.append('EndDate', formData.toDate);
      params.append('Comparison', 'No Comparison');
      params.append('PageNumber', page);
      params.append('PageSize', pagination.pageSize);

      // ✅ Always use localStorage branch id
      params.append('BranchId', resBranchId);

      // ✅ PaymentType filter (0 = all)
      params.append('PaymentType', paymentType?.value ?? 0);

      const res = await apiService.getApiParamater('GET_SALE_SUMMERY', `?${params.toString()}`);

      const list = Array.isArray(res?.data?.result) ? res.data.result : [];
      setRows(list);

      setPagination((p) => ({
        ...p,
        ...(res?.data?.paginationData || {}),
        currentPage: res?.data?.paginationData?.currentPage || page,
      }));
    } catch (e) {
      console.error(e);
      setErr('Failed to fetch report. Please try again.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChangeRange = (e) => setFormData((old) => ({ ...old, [e.target.name]: e.target.value }));

  const onSearch = () => fetchData(1);

  const gotoPage = (next) => {
    if (!next || next === pagination.currentPage) return;
    fetchData(next);
  };

  const exportCSV = () => {
    if (!safeRows.length) return;

    const headers = COLUMNS.map((c) => `"${c.label}"`).join(',');
    const body = safeRows.map((r) => COLUMNS.map((c) => `"${String(clean(r[c.key])).replace(/"/g, '""')}"`).join(',')).join('\n');

    const csv = `${headers}\n${body}`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = `category-sale-summary-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportExcel = () => {
    if (!safeRows.length) return;

    const resBranchId = getResBranchId();

    // Header
    const header = COLUMNS.map((c) => c.label);

    // Body rows (keep numeric as numbers)
    const body = safeRows.map((r) =>
      COLUMNS.map((c) => {
        const v = clean(r?.[c.key]);

        if (c.key === 'Total Sales (Qty)') return Number(v) || 0;
        if (c.key === 'Total Sales (Amount)') return Number(v) || 0;
        if (c.key === '% of Total Sales') return Number(v) || 0;

        return String(r?.[c.key] ?? '').trim();
      }),
    );

    // Totals row
    const totalsRow = ['Total', Number(totals.qty) || 0, Number(totals.amt) || 0, Number(totals.pct) || 0];

    const aoa = [header, ...body, totalsRow];
    const ws = XLSX.utils.aoa_to_sheet(aoa);

    ws['!cols'] = [
      { wch: 28 }, // Payment Category
      { wch: 16 }, // Total Sales (Qty)
      { wch: 20 }, // Total Sales (Amount)
      { wch: 16 }, // % of Total Sales
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Category Sale Summary');

    const payType = paymentType?.label ? paymentType.label.replace(/[\\/:*?"<>|]/g, '-') : 'All-Payments';
    const date = new Date().toISOString().slice(0, 10);

    XLSX.writeFile(wb, `category-sale-summary_Branch-${resBranchId}_${payType}_${formData.fromDate}_to_${formData.toDate}_${date}.xlsx`);
  };

  return (
    <div className="p-5">
      <button className="addItem-btn mb-3" onClick={() => navigate(-1)}>
        ← Back
      </button>

      {/* Filters (NO Branch Dropdown) */}
      <div className="row g-3 align-items-end">
        {/* Payment Type Dropdown */}
        <div className="col-md-3">
          <label className="form-label">Payment Type</label>
          <Select
            value={paymentType}
            onChange={setPaymentType}
            options={PAYMENT_TYPE_OPTIONS}
            placeholder="Select Payment Type"
            menuPortalTarget={document.body}
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 999999 }),
            }}
          />
        </div>

        <div className="col-md-2">
          <label className="form-label">
            {t('From')} {t('Date')}
          </label>
          <input type="date" className="form-control" name="fromDate" value={formData.fromDate} onChange={onChangeRange} />
        </div>

        <div className="col-md-2">
          <label className="form-label">
            {t('To')} {t('Date')}
          </label>
          <input type="date" className="form-control" name="toDate" value={formData.toDate} onChange={onChangeRange} />
        </div>

        <div className="col-md-2">
          <button className="addItem-btn w-100 mt-1" onClick={onSearch} disabled={loading}>
            {loading ? 'Loading…' : t('Search')}
          </button>
        </div>
      </div>

      {/* TABLE */}
      <div className="POsgridshad rounded p-4 mt-4">
        {err && <div className="alert alert-danger mb-3">{err}</div>}

        <div className="d-flex justify-content-end mb-2">
          <button className="btn btn-outline-secondary btn-sm" onClick={exportCSV} disabled={!safeRows.length}>
            Export CSV
          </button>

          <button className="btn btn-outline-success btn-sm ms-2" onClick={exportExcel} disabled={!safeRows.length}>
            Export Excel
          </button>
        </div>

        <div className="custom-table-container" style={{ maxHeight: 480, overflowY: 'auto' }}>
          <table className="custom-table">
            <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
              <tr>
                {COLUMNS.map((col) => (
                  <th key={col.key} className={col.align === 'right' ? 'text-end' : ''}>
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {!loading && safeRows.length === 0 && (
                <tr>
                  <td colSpan={COLUMNS.length} className="text-center text-muted py-4">
                    No data found
                  </td>
                </tr>
              )}

              {loading && (
                <tr>
                  <td colSpan={COLUMNS.length} className="text-center py-4">
                    Loading…
                  </td>
                </tr>
              )}

              {!loading &&
                safeRows.map((r, i) => (
                  <tr key={i}>
                    <td>{r['Payment Category'] ?? '—'}</td>
                    <td className="text-end">{numberFmt(clean(r['Total Sales (Qty)']), 0)}</td>
                    <td className="text-end">
                      {Currency?.currency?.code} {numberFmt(clean(r['Total Sales (Amount)']))}
                    </td>
                    <td className="text-end">{numberFmt(clean(r['% of Total Sales']))}%</td>
                  </tr>
                ))}
            </tbody>

            {safeRows.length > 0 && (
              <tfoot>
                <tr className="bg-light fw-bold">
                  <td>Total</td>
                  <td className="text-end">{numberFmt(totals.qty, 0)}</td>
                  <td className="text-end">
                    {Currency?.currency?.code} {numberFmt(totals.amt)}
                  </td>
                  <td className="text-end">{numberFmt(totals.pct)}%</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        {/* Pagination */}
        {(pagination?.totalRecords ?? 0) > pagination.pageSize && (
          <div className="d-flex justify-content-between align-items-center mt-3">
            <div className="btn-group">
              <button
                className="btn btn-outline-secondary"
                onClick={() => gotoPage(Math.max(1, pagination.currentPage - 1))}
                disabled={loading || pagination.currentPage <= 1}
              >
                Prev
              </button>

              <button
                className="btn btn-outline-secondary"
                onClick={() => gotoPage(pagination.currentPage + 1)}
                disabled={loading || pagination.currentPage >= pagination.totalPages}
              >
                Next
              </button>
            </div>

            <div className="text-muted small">
              Page {pagination.currentPage} of {pagination.totalPages || 1}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CategorySaleReportCashier;
